import getPinterestVariantOptions from "./pinterest";
import { getVariantId, getVariantInfo } from "./variants";

const SHOPIFY_API_KEY = "4e4e809f96ecf8dc6eff5d1fe9a4bc4c";
// Update to newer API version that supports Cart API
const SHOPIFY_GRAPHQL_ENDPOINT = "https://cart.ramblemaps.com/api/2024-04/graphql.json";

// local storage keys
const LS_CHECKOUT_URL = "rambleMapsCheckoutURL";
const LS_CHECKOUT_ID = "rambleMapsCheckoutId";
const LS_CART = "cart";

function getCartItems() {
  return JSON.parse(localStorage.getItem(LS_CART));
}

// Handle Shopify cart mutation response
function handleNewShopifyCheckout(key) {
  return function(response) {
    try {
      // The response structure is different in Cart API
      // Make sure we're checking if data exists and has the expected structure
      if (!response.data || !response.data[key] || !response.data[key].cart) {
        console.error("Unexpected response structure:", response);
        throw new Error("Invalid response structure from Shopify Cart API");
      }
      
      const checkoutUrl = response.data[key].cart.checkoutUrl;
      const checkoutButton = document.getElementById("js_checkout");
      localStorage.setItem(LS_CHECKOUT_URL, checkoutUrl);
      localStorage.setItem(LS_CHECKOUT_ID, response.data[key].cart.id)
      window.rambleMaps.updatingCheckoutUrl = false;

      // if the user is ready to checkout but is waiting on this call, redirect
      // them to checkout when it comes back
      if (window.rambleMaps.readyToCheckout) {
        window.location.href = checkoutUrl;
      }
      checkoutButton.dataset.checkoutUrl = checkoutUrl;

      // make checkoutUrl available to consumer
      return checkoutUrl;
    } catch (error) {
      console.error("Error processing cart response:", error, response);
      
      // if we are here, it is because we've gotten a valid HTTP response from
      // shopify, but it was not a response we expected. We likely had an error
      // parsing the response.

      // errors is an array of objects with a message key
      if (response.errors
        && response.errors.length
        && response.errors.find(error => error.message === "Checkout is already completed.")) {
        // checkout is already completed, clear cart and remove checkout from local storage
        localStorage.removeItem(LS_CHECKOUT_URL);
        localStorage.removeItem(LS_CHECKOUT_ID);
        localStorage.removeItem(LS_CART);
      }

      window.Sentry.captureException(error, {
        extra: {
          key: key,
        },
        contexts: {
          response: response,
          localStorageCart: getCartItems()
        }
      });
    }
  }
}

function fetchAndUpdateShopifyCheckout(query, mutationKey) {
  return fetch(SHOPIFY_GRAPHQL_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Shopify-Storefront-Access-Token": SHOPIFY_API_KEY
    },
    body: JSON.stringify({
      query: query,
    })
  })
    .then(response => {
      if (!response.ok) {
        throw new Error("Bad HTTP response from Shopify");
      }
      return response;
    })
    .then(r => r.json())
    .then(handleNewShopifyCheckout(mutationKey))
    .catch(error => {
      window.Sentry.captureException(error, {
        extra: {
          key: mutationKey,
        },
        contexts: {
          localStorageCart: getCartItems()
        }
      });
    })
}

// Find item in cart of given ID and increment its quantity by 1
function incrementCartItem(cart, itemIdx) {
  cart[itemIdx].quantity = cart[itemIdx].quantity ? ++cart[itemIdx].quantity : 2;
  return cart;
}

// rambleCart is responsible for adding and removing items to our shopping cart,
// determining when the cart should be displayed, and passing
// along our cart contents to shopify and providing our users with a link to
// the shopify cart.
//
// The cart itself is a modal that appears when the user:
// - has clicked "add to cart" OR
// - clicks on the cart icon in the header
//
// The cart inventory is stored in local storage. Each time a change is made to
// the cart, the cart is updated in local storage.
//
// An item is added to the cart by seeing which option buttons are selected and
// reading a data attribute from that item which is used to lookup the variant
// represented by those options.
export default function rambleCart() {
  const cart = document.getElementById("js_cart");
  const cartIcon = document.getElementById("js_cart_icon");
  const cartCount = document.getElementById("js_cart_count");
  const checkoutButton = document.getElementById("js_checkout");
  const userAgent = window.navigator.userAgent;

  // in iOS Safari, the bottom 44px are reserved for the browser bar. This prevents
  // the checkout button from working in many situations. Below we target iOS
  // Safari for a hack to always show the browser bar.
  const isMobileSafari =
    userAgent.includes  // IE 11 doesnt' have string.includes
    && userAgent.includes("Mobile")
    && userAgent.includes("Safari")
    && !userAgent.includes("Chrome")
    && !userAgent.includes("CriOS");

  // Update the cart item count and visibility
  function updateCartCount(items) {
    if (items && items.length) {
      // sum over all of the quantities in the cart
      cartCount.innerHTML = items.reduce((total, item) => {
        return total + (item.quantity || 1);
      }, 0);
      cartIcon.classList.add("not_empty");
    } else {
      cartIcon.classList.remove("not_empty");
      cartCount.innerHTML = "0";
    }
  }

  function setCheckoutButtonActive(setActive) {
    if (setActive) {
      checkoutButton.classList.remove("inactive");
      checkoutButton.disabled = false;
    } else {
      checkoutButton.classList.add("inactive");
      checkoutButton.disabled = true;
    }
  }

  function closeCart() {
    cart.classList.remove("open");

    // In Safari the bottom 44 pixels are reserved for the bottom browser bar
    // so we trigger a scroll in order to make the bar visible when we open
    // the cart
    if (isMobileSafari) {
      document.documentElement.classList.remove("ios_safari_cart_open");
    }
  }

  function makeCartFooterFixed() {
    const cartFooter = document.getElementById("js_cart_footer");
    const footerStyle = window.getComputedStyle(cartFooter);
    const cartHeader = document.getElementById("js_cart_header");
    const headerStyle = window.getComputedStyle(cartHeader);
    const cartItems = document.getElementById("js_cart_items");
    const cartItemsStyle = window.getComputedStyle(cartItems);

    // Calculate space available to cart items
    const headerVerticalSpace = parseInt(headerStyle.height)
      + parseInt(headerStyle.paddingTop)
      + parseInt(headerStyle.paddingBottom)
      + parseInt(headerStyle.marginTop)
      + parseInt(headerStyle.marginBottom);
    const footerVerticalSpace = parseInt(footerStyle.height)
      + parseInt(footerStyle.paddingTop)
      + parseInt(footerStyle.paddingBottom)
      + parseInt(footerStyle.marginTop)
      + parseInt(footerStyle.marginBottom);
    const cartItemsAvailableSpace = window.innerHeight - (
      headerVerticalSpace + footerVerticalSpace);

    // Determine space taken up by cart items
    const cartItemsVerticalSpace = parseInt(cartItemsStyle.height)
      + parseInt(cartItemsStyle.paddingTop)
      + parseInt(cartItemsStyle.paddingBottom)
      + parseInt(cartItemsStyle.marginTop)
      + parseInt(cartItemsStyle.marginBottom);

    // Add scrollable items, if we need the space
    if (cartItemsVerticalSpace > cartItemsAvailableSpace) {
      document.getElementById("js_cart_items").style.height = `${cartItemsAvailableSpace}px`;
      document.getElementById("js_cart_items").style.overflowY = "scroll";
      cartFooter.style.boxShadow = "0 -2px 4px -4px rgba(0, 0, 0, 0.5)";
      cartHeader.style.boxShadow = "0 2px 4px -4px rgba(0, 0, 0, 0.75)";
    }

    const cartContainer = document.getElementById("js_cart_container");
    cartFooter.style.position = "fixed";
    cartFooter.style.width = `${cartContainer.clientWidth}px`;
    cartFooter.style.bottom = 0;
  }

  function openCart() {
    cart.classList.add("open");

    // In Safari the bottom 44 pixels are reserved for the bottom browser bar
    // so we trigger a scroll in order to make the bar visible when we open
    // the cart
    if (isMobileSafari) {
      document.documentElement.classList.add("ios_safari_cart_open");
    }

    cartIcon.classList.remove("show");
    makeCartFooterFixed();
  }

  function generateMarkupForItems(
    markup,
    itemInfo,
    index
  ) {
    const {
      price,
      id,
      material,
      size,
      title,
      img,
      href,
      titleBox,
      optionalTitle,
      isGiftCard,
      quantity = 1
    } = itemInfo;
    let itemDescription;

    if (isGiftCard) {
      itemDescription = `Ramble Maps Gift Card - $${price}`;
    } else {
      // if titlebox is optional, show choice
      itemDescription = optionalTitle
        ? `${size[0]}" x ${size[1]}" / ${material} / ${titleBox ? "With Title" : "No Title"}`
        : `${size[0]}" x ${size[1]}" / ${material}`;
    }

    return `
      ${markup}
        <div class="cart_item">
          <div class="cart_item_image">
            <a href="${href}">
              <img src="${img}" alt="${title} Thumbnail"></img>
            </a>
          </div>
          <div class="cart_item_info">
            <div class="cart_item_product_name"><a href="${href}">${title}</a></div>
            <div class="cart_item_variant_name">${itemDescription}</div>
            <div class="cart_item_incrementors">
              <svg data-index="${index}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="cart_item_add_one w-8 mr-4 icon-add-square"><rect width="18" height="18" x="3" y="3" class="primary" rx="2"></rect><path class="secondary" d="M13 11h4a1 1 0 0 1 0 2h-4v4a1 1 0 0 1-2 0v-4H7a1 1 0 0 1 0-2h4V7a1 1 0 0 1 2 0v4z"></path></svg>
              <span id="quantity_${id}" class="cart_item_quantity">${quantity}</span>
              <svg data-index="${index}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="cart_item_remove_one w-8 mr-4 icon-remove-square"><rect width="18" height="18" x="3" y="3" class="primary" rx="2"></rect><rect width="12" height="2" x="6" y="11" class="secondary" rx="1"></rect></svg>
              <span class="cart_item_remove" data-index="${index}">Remove</span>
            </div>
          </div>
          <div class="cart_item_cost">$${price}</div>
        </div>
      </div>
    `;
  }


  // Tell shopify about the items in our cart. If we already have a checkout
  // URL and ID in localStorage, update that checkout, otherwise create a
  // new one.
  // Returns a promise with the checkout URL so our add to cart handler can
  // pass that URL to klaviyo.
  function updateShopifyCheckout(lineItems) {
    // We want to know when we're actively updating a checkout so we don't
    // accidentally send the user to an old checkout.
    window.rambleMaps.updatingCheckoutUrl = true;

    // Always create a new cart with the current items
    // This is simpler than trying to update an existing cart
    // which would require knowing the line IDs
    if (lineItems && lineItems.length) {
      // Changed from checkoutCreate to cartCreate
      const mutationKey = "cartCreate";
      // Updated query format for Cart API
      const query = `mutation {
        ${mutationKey}(input: {
          lines: [${lineItems}]
        }) {
          cart {
            id
            checkoutUrl
          }
          userErrors {
            field
            message
          }
        }
      }`;

      // Create a new cart each time
      return fetchAndUpdateShopifyCheckout(query, mutationKey);
    }
    
    // Return a resolved promise with null for empty carts
    return Promise.resolve(null);
  }

  // Update the HTML associated with the cart, both the contents of the cart
  // and the cart icon in the header
  function updateCartHTML(items) {
    const cartItems = document.getElementById("js_cart_items");

    updateCartCount(items);

    // replace cart's markup with markup for all items in local storage
    if (items && items.length) {
      // if we have items, checkout button should be active
      setCheckoutButtonActive(true);

      // generate HTML for items in cart, replace inner HTML of cart items
      cartItems.innerHTML = items.reduce(generateMarkupForItems, "");

      // Update cart subtotal
      const subtotal = items.reduce((sum, { quantity = 1, price }) => sum + quantity * price, 0);
      document.getElementById("js_subtotal").innerHTML = `$${subtotal}`;

    // we have no items in local storage, show empty cart view
    } else {
      cartItems.innerHTML = `
        <div class="empty_cart">
          <div class="sad_face">&#128546;</div>
          <p>There's nothing in your cart.</p>
          <p>Check out our <a href="/maps">available maps</a> to find something you like.</p>
        </div>`;

      document.getElementById("js_subtotal").innerHTML = "$0";
      setCheckoutButtonActive(false);
    }
  }

  // Update the cart from local storage.
  // - Update the HTML based on our cart items
  // - Set url on checkout button so that user can get to their checkout
  // - let Shopify know what is in our cart so that the user can checkout.
  // Returns promise with new cart URL if we have items in cart
  function updateCart() {
    const items = getCartItems() || [];
    updateCartHTML(items);

    if (items.length === 0) {
      // Don't make API calls for empty carts
      return Promise.resolve(null);
    }

    // Format for cartCreate - merchandiseId is the product variant ID
    const lineItems = items.map(({id, quantity = 1}) => `{ merchandiseId: "${id}", quantity: ${quantity} }`);
    return updateShopifyCheckout(lineItems);
  }

  function handleIncrementCartItem(e) {
    e.stopPropagation();

    const addOneEl = e.target.closest(".cart_item_add_one");
    if (addOneEl) {
      const cartData = incrementCartItem(getCartItems(), addOneEl.dataset.index);
      const addedItem = cartData[addOneEl.dataset.index];
      localStorage.setItem(LS_CART, JSON.stringify(cartData));
      updateCart();

      // Event for audiences
      window.gtag("event", "click", {
        event_category: "incrementItem",
        ecomm_pagetype: "cart",
      });

      // this event is of the structure specified in the "Enhanced Ecommerce"
      // documentation and allows us to get nice reporting
      // https://developers.google.com/analytics/devguides/collection/gtagjs/enhanced-ecommerce
      window.gtag("event", "add_to_cart", {
        items: [
          {
            id: addedItem.id,
            variant: addedItem.sku || "",
            category: addedItem.collection || "",
            name: addedItem.title,
            quantity: 1,
            price: addedItem.price
          }
        ]
      });
    }
  }

  function handleDecrementCartItem(e) {
    const removeOneEl = e.target.closest(".cart_item_remove_one");
    if (removeOneEl) {
      const cartData = getCartItems();
      const itemIdx = removeOneEl.dataset.index;
      const removedItem = cartData[itemIdx];

      // decrement the quantity, checking for existing value first to ensure
      // backwards compatibility with carts that had items in them before we
      // introduced the quantity field
      cartData[itemIdx].quantity = cartData[itemIdx].quantity
        ? --cartData[itemIdx].quantity : 0;

      // filter out any items with a quantity value of 0. To ensure
      // backwards compatibility we check for the existince of the
      // quantity key first
      const filteredCart = cartData.filter(item => !("quantity" in item && item.quantity === 0));

      localStorage.setItem(LS_CART, JSON.stringify(filteredCart));
      updateCart();

      // Event for audiences
      window.gtag("event", "click", {
        event_category: "decrementItem",
        ecomm_pagetype: "cart",
      });

      // Event for enhanced ecommerce report
      window.gtag("event", "remove_from_cart", {
        items: [
          {
            id: removedItem.id,
            name: removedItem.title,
            quantity: 1,
            price: removedItem.price,
            variant: removedItem.sku || "",
            category: removedItem.collection || ""
          }
        ]
      });

      e.stopPropagation();
    }
  }

  function handleRemoveCartItem(e) {
    const removeItemEl = e.target.closest(".cart_item_remove");
    if (removeItemEl) {
      const cartData = getCartItems();
      // remove the item at index, re-index cartData
      const removedItem = cartData.splice(removeItemEl.dataset.index, 1);
      localStorage.setItem(LS_CART, JSON.stringify(cartData));
      updateCart();

      // Event for audiences
      window.gtag("event", "click", {
        event_category: "removeItem",
        ecomm_pagetype: "cart",
      });

      // Event for enhanced ecommerce report
      window.gtag("event", "remove_from_cart", {
        items: [
          {
            id: removedItem.id,
            name: removedItem.title,
            quantity: removedItem.quantity,
            price: removedItem.price,
            variant: removedItem.sku || "",
            category: removedItem.collection || ""
          }
        ]
      });

      e.stopPropagation();
    }
  }

  function scrollToBottomOfCart() {
    document.getElementById("js_cart_items").scrollTop = document.getElementById("js_cart_items").scrollHeight;
  }

  function showConfirmDeliveryPopup() {
    const deliveryPopup =
      document.querySelector(".js_confirm_delivery_modal");
    if (deliveryPopup) {
      deliveryPopup.style.display = "block";
    }
  }

  function hideConfirmDeliveryPopup() {
    const deliveryPopup =
      document.querySelector(".js_confirm_delivery_modal");
    if (deliveryPopup) {
      deliveryPopup.style.display = "none";
    }
  }

  // We want to make sure that the user is okay with delivery after 12/25
  function confirmDeliveryDate(e) {
    e.stopPropagation();
    showConfirmDeliveryPopup();
  }

  function addToCartClick(e) {
    e.stopPropagation();
    openCart();
    setCheckoutButtonActive(true);

    const variantInfo = getVariantInfo();
    const { price, id, size, material, titleBox, sku, isGiftCard } = variantInfo;
    const decodedId = getVariantId(id);

    let cartData = getCartItems() || [];

    // if our cart already has an item with this variant ID in it, increment
    // the item in the cart, otherwise add it to the cart
    const existingItem = cartData.findIndex(item => item.id === id);
    // findIndex returns -1 if no match
    if (existingItem >= 0) {
      cartData = incrementCartItem(cartData, existingItem);
    } else {
      cartData.push({
        price,
        id,
        quantity: 1,
        size,
        titleBox,
        sku,
        isGiftCard,
        optionalTitle: window.rambleMaps.optionalTitle,
        collection: window.rambleMaps.collection,
        material: window.rambleMaps.materials && window.rambleMaps.materials.find(
          ({ id: materialId }) => material === materialId
        ).name,
        title: window.rambleMaps.title,
        img: window.rambleMaps.productImg,
        href: window.location.href
      });
    }

    // update the cart in local storage
    localStorage.setItem(LS_CART, JSON.stringify(cartData));

    makeCartFooterFixed();
    scrollToBottomOfCart();

    // update cart, get promise to new checkout url
    const newCheckoutUrl = updateCart();

    // publish add-to-cart related events unless we're on a gift card page
    if (!window.rambleMaps.isGiftCardPage) {
      // tell klaviyo about our checkout URL so user can complete their purchase
      // via klaviyo-sent email
      newCheckoutUrl.then(checkoutUrl => {
        const _learnq = window._learnq || [];
        const items = getCartItems();

        _learnq.push(["track", "Added to Cart", {
          $value: price,
          AddedItemProductName: window.rambleMaps.title,
          AddedItemProductId: decodedId,
          AddedItemImageUrl: window.rambleMaps.productImg,
          AddedItemPrice: price,
          AddedItemQuantity: 1,
          CheckoutUrl: checkoutUrl,
          Items: items.map(item => {
            return {
              ProductId: getVariantId(item.id),
              ProductName: item.title,
              Quantity: 1,
              ItemPrice: item.price,
              ProductUrl: item.href,
              ImageUrl: item.img,
              ProductSize: item.size ? `${item.size[0]}x${item.size[1]}` : null,
              ProductMaterial: item.material || null
            };
          })
        }]);
      });

      // Fire events
      window.pintrk('track', 'addtocart', {
        value: price,
        currency: 'USD',
        ...getPinterestVariantOptions(variantInfo)
      });

      window.fbq('track', 'AddToCart', {
        value: price,
        currency: 'USD',
        content_type: 'product',
        content_ids: [window.rambleMaps.productId]
      });

      // this event is of the structure specified in the "Enhanced Ecommerce"
      // documentation and allows us to get nice reporting
      // https://developers.google.com/analytics/devguides/collection/gtagjs/enhanced-ecommerce
      window.gtag("event", "add_to_cart", {
        items: [
          {
            id: `${window.rambleMaps.productId}`,
            variant: sku,
            name: `${window.rambleMaps.title}`,
            quantity: 1,
            category: `${window.rambleMaps.collection}`,
            price
          }
        ]
      });

      // This event is needed for audience generation. It isn't great for
      // analytics
      window.gtag("event", "click", {
        event_category: "addToCart",
        ecomm_pagetype: "cart",
        ecomm_label: sku,
        value: price
      });
    }
  }

  const SHOW_NO_HOLIDAY_DELIVERY_MODAL = false;

  function setupEventListeners() {
    const addToCartBtn = document.getElementById("js_add_to_cart");
    if (addToCartBtn) {
      // only show delivery deadline modal if we have a physical map.
      // gift cards should add to cart with no modal
      if (window.rambleMaps.isGiftCardPage || !SHOW_NO_HOLIDAY_DELIVERY_MODAL) {
        addToCartBtn.addEventListener("click", addToCartClick);
      } else {
        addToCartBtn.addEventListener("click", confirmDeliveryDate);
      }
    }

    const closeCartIcon = document.getElementById("js_close_cart");
    closeCartIcon.addEventListener("click", closeCart);

    cartIcon.addEventListener("click", (e) => {
      e.stopPropagation();
      openCart();
      makeCartFooterFixed();
    });

    // click event on body should close cart if open
    document.body.addEventListener("click", closeCart);

    // click event on cart shouldn't propagate up to body, where there is an
    // event to close an open cart
    cart.addEventListener("click", (e) => {
      e.stopPropagation();
    });

    checkoutButton.addEventListener("click", () => {
      // if button is disabled, don't try and checkout, it means cart is empty
      if (checkoutButton.disabled) {
        return;
      }

      // if we're not updating the checkout, go ahead and go to checkout
      if (!window.rambleMaps.updatingCheckoutUrl) {
        window.location.href = checkoutButton.dataset.checkoutUrl;
      // let the call to shopify return and handle redirecting us to checkout
      } else {
        window.rambleMaps.readyToCheckout = true;
      }
    });

    const lateDeliveryOk = document.querySelector(".js_delivery_date_ok");
    if (lateDeliveryOk) {
      lateDeliveryOk.addEventListener("click", (e) => {
        e.stopPropagation();
        hideConfirmDeliveryPopup();
        addToCartClick(e);
      });
    }
    const lateDeliveryNotOk =
      document.querySelector(".js_delivery_date_not_ok");
    if (lateDeliveryNotOk) {
      lateDeliveryNotOk.addEventListener("click", hideConfirmDeliveryPopup);
    }
    const deliveryModalBackground =
      document.querySelector(".js_confirm_delivery_modal");
    if (deliveryModalBackground) {
      deliveryModalBackground.addEventListener("click", (e) => {
        e.stopPropagation();
        // Only want to close the modal if the click is on the background.
        // If the click were on the modal text or something, we don't want to
        // close.
        if (e.target === deliveryModalBackground) {
          hideConfirmDeliveryPopup();
        }
      });
    }

    const cartItems = document.getElementById("js_cart_items");
    cartItems.addEventListener("click", handleIncrementCartItem);
    cartItems.addEventListener("click", handleDecrementCartItem);
    cartItems.addEventListener("click", handleRemoveCartItem);
  }

  // The following cookie is set on shopify checkout:
  // "rambleCheckout=true;path=/;samesite=strict;secure;max-age=31536000;domain=ramblemaps.com";
  // If we had a successful checkout, we want to empty our cart from local storage
  // and reset the cookie
  function checkForCartCookie() {
    const userCheckedOut = document.cookie.split(";").some(item => (
      item.trim().indexOf("rambleCheckout=true") === 0
    ));
    if (userCheckedOut) {
      // clear local storage
      window.localStorage.removeItem(LS_CART);
      window.localStorage.removeItem(LS_CHECKOUT_URL);
      window.localStorage.removeItem(LS_CHECKOUT_ID);
      // expire cookie
      document.cookie = "rambleCheckout=;path=/;max-age=-1;domain=ramblemaps.com;expires=Thu, 01 Jan 1970 00:00:00 GMT";
    }
  }

  // run on page load
  setupEventListeners();
  checkForCartCookie();
  updateCart();
}